import { isCenterpiece, isCollage, isPhoto } from '@mixtiles/web-backend-shared'
import { Dialog } from '@mui/material'
import axios from 'axios'
import classNames from 'classnames'
import { clearTileWarningsCache } from 'pages/PhotoStyler/TileWarning/tileWarningsCache'
import {
  type ComponentProps,
  type ForwardedRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { type PaymentIntent } from '@stripe/stripe-js'
import { useStripe } from '@stripe/react-stripe-js'
import { experimentManager } from 'services/ExperimentManager/ExperimentManager'
import { useAccount } from '../../components/Account/AccountProvider'
import BasicDialogButton from '../../components/MixtilesDialog/BasicDialogButton/BasicDialogButton'
import BasicDialogContent from '../../components/MixtilesDialog/BasicDialogContent/BasicDialogContent'
import MixtilesDialog from '../../components/MixtilesDialog/MixtilesDialog'
import { saveCheckoutBackup } from 'services/CheckoutBackup/checkoutBackupService'
import { getTileCropType } from '../../components/PhotoBank/tileUtils'
import PostPurchaseSuccess from '../../components/PostPurchaseSuccess/PostPurchaseSuccess'
import { convertAddressToStripeFormat } from '../../components/StripeAddressForm/stripeAdapter'
import {
  HOME_URL,
  ORDER_SUMMARY_V2_URL,
  POST_PAYMENT_QUERY_PATHNAME,
  POST_PAYMENT_URL,
} from '../../config/config'
import { generateCollageImageUpload } from '../../services/Collage/collageImageUpload'
import { IS_COLLAGE_CREATION_AVAILABLE } from '../../services/Collage/collage.consts'
import { analytics, EVENT_NAMES } from '../../services/Analytics/Analytics'
import { getProductTypeForEvent } from '../../services/Analytics/orderAnalytics.js'
import { intercomService } from '../../services/Analytics/platforms/intercom'
import {
  ATTRIBUTION_SOURCE,
  attributionSurveyManager,
} from '../../services/AttributionSurveyManager'
import {
  detectBadCrop,
  shouldDetectBadCrop,
} from '../../services/badCropDetection'
import { logger } from '../../services/logger'
import { useNavigate, useSearchParams } from 'react-router'
import OrderManager from '../../services/OrderManager'
import { createGroupOrder } from '../../services/GroupOrderManager'
import { resetOrderSessionId } from '../../services/OrderSession'
import PaymentManager from '../../services/PaymentManager'
import { PaymentMethodType } from '../../services/PaymentManager.types'
import { serializePhotoBooksMetadata } from '../../services/PhotoBook/photoBookDTO'
import { toRuntimeCover } from './photoBookOrderCover'
import { photosInPages } from '../../services/PhotoBook/photoBookUtils'
import { isPhotoCover } from '../../components/PhotoBook/PhotoBookProvider/PhotoBook.utils'
import { useProductType } from '../../services/ProductTypesManager'
import { PRODUCT_TYPES } from '../../services/ProductTypeState'
import { isGiftCardOrder } from '../../pages/GiftCard/utils'
import { promoCodeManager } from '../../services/PromoCodeManager'
import { sentryService } from '../../services/SentryService'
import { taskManager } from '../../services/TaskManager'
import { useTranslation } from 'react-i18next'
import { clearCartId } from '../../services/UserCart/userCartService'
import { clearUserEmail } from '../../services/userEmail'
import { FLOWS } from '../../services/UserCart/userCart.consts'
import { isReusablePaymentMethod } from '../../stores/paymentStore/payment.utils'
import { shouldShowDeliveryRange } from '../../stores/shippingStore/shipping.utils'
import {
  extractApiError,
  extractErrorMessage,
  mixtilesAxios,
} from '../../utils/ApiUtils'
import {
  formatIsoDateRange,
  isoToDeliveryDate,
} from '../../utils/DateTimeUtils'
import { getUrlParam } from '../../utils/UrlUtils'
import { sleep } from '../../utils/utils'
import CompleteOrderErrorDialog from './CompleteOrderErrorDialog'
import './CompletingOrder.scss'
import * as S from './CompletingOrder.styles'
import {
  getStripeErrorProperties,
  isStripeCardError,
} from './paymentErrorAnalytics'
import {
  clearCreateOrderParams,
  getCompletedPaymentIntentIds,
  PAYMENT_INTENT_PARAM,
  restoreCreateOrderParams,
  storeCompletedPaymentIntentIds,
  storeCreateOrderParams,
} from './completingOrderSaver'
import PostPurchaseAttribution from './PostPurchaseAttribution/PostPurchaseAttribution'
import UploadsFailedPopup from './UploadsFailedPopup/UploadsFailedPopup'
import { prepareUpsellCollage } from '../../services/TileUpsell/tileUpsellUtils'
import {
  usePhotoBankStore,
  useTilesStore,
} from '../../stores/RootStoreProvider'
import { getCartStore } from '../../stores/cartStore/CartStoreProvider'
import { getFreshIdToken } from '../../services/AuthProvider/cognito'
import { getAuthRequestConfig } from '../../services/LoginUtills'
import { observer } from 'mobx-react'
import {
  readMobxSnapshot,
  readOutsideReaction,
} from 'utils/mobx/readOutsideReaction'
import { type PhotoBankStore } from '../../stores/photoBankStore/PhotoBankStore'
import { type Address } from '../../stores/addressStore/address.types'
import { type CreateOrderParams } from '../../services/OrderManager.types'
import { type ShippingOption } from '../../stores/shippingStore/shipping.types'
import { type CheckoutSummary } from '../../components/Pricing/types/checkout.types'
import {
  type CheckoutContextValue,
  useCheckout,
} from '../../components/Checkout/CheckoutContext'
import { type GroupPricingProduct } from '../../features/cart/groupCheckoutPricing.types'

async function resolvePhotoBookMetadata(
  item: {
    metadata: Pick<
      Parameters<typeof serializePhotoBooksMetadata>[0],
      'bookId' | 'bookSize' | 'cover' | 'pages'
    >
  },
  photoBankStore: PhotoBankStore
) {
  const { bookId, bookSize, cover, pages } = item.metadata
  const allBookPhotos = [
    ...photosInPages(pages),
    isPhotoCover(cover) ? cover.photo : undefined,
  ].filter((photo) => photo !== undefined)
  const remotePhotos = await Promise.all(
    allBookPhotos.map((p) =>
      readOutsideReaction(() =>
        photoBankStore
          .getRemotePhotoAsync(p.uid)
          .then((upload) => readMobxSnapshot(() => upload))
      )
    )
  )
  return {
    allBookPhotos,
    remotePhotos,
    serialized: serializePhotoBooksMetadata({
      bookId,
      bookSize,
      // #7296: serialize from the runtime cover shape — checkout payloads
      // persisted before the shapes converged (sessionStorage, checkout backup)
      // keep the AI image under aiGeneratedCoverUrl.
      cover: toRuntimeCover(cover),
      pages,
      remotePhotos: remotePhotos.filter((photo) => photo !== undefined),
    }),
  }
}

const RETRY_INTERVAL = 800
const CompletionState = {
  PROGRESS: 0,
  PROGRESS_COMPLETED: 1,
  CHECKMARK: 2,
  DONE: 3,
} as const

const CLIENT_SECRET_PARAM = 'payment_intent_client_secret'
const REDIRECT_STATUS_PARAM = 'redirect_status'
// `redirect_status=failed` reliably means the PI is still requires_payment_method (capture would fail).
const REDIRECT_STATUS_FAILED = 'failed'

// PI statuses safe to proceed with capture. Logging only — client PI status is often unavailable (Stripe.js not loaded).
const CAPTURABLE_PI_STATUSES = new Set<PaymentIntent.Status>([
  'succeeded',
  'requires_capture',
  'processing',
])

let isHandlingPostRedirectPayment = false

function isGoogleReadAloud() {
  if (typeof navigator !== 'undefined') {
    return navigator.userAgent.includes('Google-Read-Aloud')
  } else {
    return false
  }
}

type CompletingOrderProps = {
  isCartFlow: boolean
}

export type CompletingOrderHandle = {
  createOrder: (createOrderParams: CreateOrderParams) => Promise<void>
  handlePostRedirectPayment: (paymentIntentId: string) => Promise<void>
}

function CompletingOrder(
  props: CompletingOrderProps,
  ref: ForwardedRef<CompletingOrderHandle>
) {
  const navigate = useNavigate()
  const [, setSearchParams] = useSearchParams()
  const { productType } = useProductType()
  const account = useAccount()
  const tilesStore = useTilesStore()
  const photoBankStore = usePhotoBankStore()
  const { t } = useTranslation()
  const { openCheckout, handleOrderComplete } = useCheckout()
  const stripe = useStripe()
  const [open, setOpen] = useState(false)
  const [extraProgress] = useState(0)
  const [completionState, setCompletionState] = useState<
    (typeof CompletionState)[keyof typeof CompletionState] | null
  >(null)
  const [
    showPaymentIntentAuthenticationError,
    setShowPaymentIntentAuthenticationError,
  ] = useState(false)
  const [showCardDeclinedError, setShowCardDeclinedError] = useState(false)
  const [showChargeFailedError, setShowChargeFailedError] = useState(false)
  const [showInvalidPromoCodeError, setShowInvalidPromoCodeError] =
    useState(false)
  const [showPriceMismatchError, setShowPriceMismatchError] = useState(false)
  const [showGeneralPaymentErrorDialog, setShowGeneralPaymentErrorDialog] =
    useState(false)
  const [showInvalidEmailError, setShowInvalidEmailError] = useState(false)
  const [isCheckoutAlreadyUsed, setIsCheckoutAlreadyUsed] = useState(false)
  const [showUploadsFailedError, setShowUploadsFailedError] = useState(false)
  const [failedPhotoUids, setFailedPhotoUids] = useState<string[]>([])
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)
  const [showAttributionSurvey, setShowAttributionSurvey] = useState(false)
  const [initialTilesToUploadCount, setInitialTilesToUploadCount] =
    useState<number>()
  // An order placed while checkout's dates weren't up to date has no date to show.
  const [deliveryPromise, setDeliveryPromise] = useState<ShippingOption>()
  const [deliveryRange, setDeliveryRange] =
    useState<ReturnType<CheckoutContextValue['getDeliveryRange']>>()
  const [orderRetry, setOrderRetry] = useState<
    | { kind: 'resubmit'; createOrderParams: CreateOrderParams }
    | { kind: 'reopen-checkout' }
  >()
  const stripeRef = useRef(stripe)

  useEffect(() => {
    stripeRef.current = stripe
  }, [stripe])

  const removePaymentIntentParams = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        ;[
          PAYMENT_INTENT_PARAM,
          CLIENT_SECRET_PARAM,
          REDIRECT_STATUS_PARAM,
        ].forEach((param) => next.delete(param))
        return next
      },
      { replace: true }
    )
  }

  // PI status from Stripe, or null if undeterminable (no secret / Stripe not loaded / error).
  const _getPaymentIntentStatus = async (clientSecret: string | null) => {
    const stripe = stripeRef.current
    if (!clientSecret || !stripe) return null
    try {
      const { paymentIntent, error } =
        await stripe.retrievePaymentIntent(clientSecret)
      if (error) {
        logger.warning('Failed to retrieve PaymentIntent', { error })
        return null
      }
      return paymentIntent?.status ?? null
    } catch (error) {
      logger.warning('Failed to retrieve PaymentIntent', { error })
      return null
    }
  }

  // As above, but retries while Stripe.js finishes loading (useStripe() lands a beat after the post-redirect mount).
  const _getPaymentIntentStatusWithRetry = async (
    clientSecret: string | null,
    { retries = 5, delayMs = 300 } = {}
  ) => {
    for (let attempt = 0; attempt < retries; attempt++) {
      const status = await _getPaymentIntentStatus(clientSecret)
      if (status) {
        if (attempt > 0) {
          logger.info('Resolved PaymentIntent status after retrying', {
            status,
            attempts: attempt + 1,
          })
        }
        return status
      }
      await sleep(delayMs)
    }
    const status = await _getPaymentIntentStatus(clientSecret)
    logger.warning('PaymentIntent status still unavailable after retries', {
      status,
      attempts: retries + 1,
    })
    return status
  }

  // Completes (or fails) a redirect-based payment. Called via ref from Checkout
  // once we've navigated off /post-payment. See the flow doc in PostPaymentCompletingOrder.js.
  const handlePostRedirectPayment = async (paymentIntentId: string) => {
    if (isHandlingPostRedirectPayment) {
      logger.warning('Already handling post redirect payment, skipping', {
        paymentIntentId,
      })
      return
    }

    if (isGoogleReadAloud()) {
      logger.warning(
        'Read Aloud detected, skipping post redirect payment handling',
        { paymentIntentId }
      )
      return
    }

    // Claim the guard synchronously (before any await) so overlapping calls can't run order creation in parallel.
    isHandlingPostRedirectPayment = true

    try {
      // Stored params are no proof the order didn't complete: completion clears them, then the Redis
      // backup refills them on the next return. The completed list is the only reliable signal here.
      if (getCompletedPaymentIntentIds().includes(paymentIntentId)) {
        logger.warning(
          'Post-redirect payment already completed, skipping order creation',
          { paymentIntentId }
        )
        removePaymentIntentParams()
        return
      }

      // redirect_status is the signal when present; if missing, fall back to the PI status (waiting for Stripe.js).
      const clientSecret = getUrlParam(CLIENT_SECRET_PARAM)
      const redirectStatus = getUrlParam(REDIRECT_STATUS_PARAM)
      let piStatus = await _getPaymentIntentStatus(clientSecret)
      if (!redirectStatus && !piStatus) {
        piStatus = await _getPaymentIntentStatusWithRetry(clientSecret)
      }
      logger.info('Post-redirect payment status check', {
        paymentIntentId,
        redirectStatus,
        piStatus,
        isCapturable: piStatus
          ? CAPTURABLE_PI_STATUSES.has(piStatus)
          : 'unknown',
        productType,
      })

      // Customer didn't complete auth — show Charge Failed, clear state, don't attempt capture.
      if (redirectStatus === REDIRECT_STATUS_FAILED) {
        logger.warning('Post-redirect payment failed, skipping capture', {
          paymentIntentId,
          redirectStatus,
          piStatus,
        })
        analytics.track(EVENT_NAMES.CHARGE_FAILED, {
          Source: 'Post Redirect Failed',
          'PI Status': piStatus,
        })
        clearCreateOrderParams(paymentIntentId)
        removePaymentIntentParams()
        setOpen(true)
        setShowChargeFailedError(true)
        return
      }

      // No redirect_status, but Stripe says the PI isn't capturable — treat as a failure.
      if (
        !redirectStatus &&
        piStatus &&
        !CAPTURABLE_PI_STATUSES.has(piStatus)
      ) {
        logger.warning(
          'Post-redirect PaymentIntent not capturable, skipping capture',
          { paymentIntentId, redirectStatus, piStatus }
        )
        analytics.track(EVENT_NAMES.CHARGE_FAILED, {
          Source: 'Post Redirect Not Capturable',
          'PI Status': piStatus,
        })
        clearCreateOrderParams(paymentIntentId)
        removePaymentIntentParams()
        setOpen(true)
        setShowChargeFailedError(true)
        return
      }

      const createOrderParams = restoreCreateOrderParams(paymentIntentId)
      if (!createOrderParams) {
        logger.warning(
          'Cannot complete post-redirect payment: order params are missing',
          { paymentIntentId }
        )
        // Nothing to capture — strip params so a reload doesn't re-enter.
        removePaymentIntentParams()
        return
      }
      const {
        tiles,
        deliveryPromise,
        hasUpToDateDeliveryDates,
        deliveryRange,
      } = createOrderParams

      setOpen(true)
      setCompletionState(CompletionState.PROGRESS)
      setDeliveryPromise(hasUpToDateDeliveryDates ? deliveryPromise : undefined)
      setDeliveryRange(deliveryRange)
      // The restored params hold the photo books already serialized for the
      // backend, which createOrder can't serialize again, so retry via checkout.
      setOrderRetry({ kind: 'reopen-checkout' })

      const { order, shouldApplyUpsellPromo } =
        (await _createOrderInBackend({
          createOrderParams,
          paymentIntentId,
        })) || {}
      if (order) {
        await _onOrderComplete(
          createOrderParams,
          paymentIntentId,
          shouldApplyUpsellPromo,
          tiles
        )
      } else {
        // Capture failed (dialog already shown) — clear state so a reload doesn't replay it.
        clearCreateOrderParams(paymentIntentId)
        removePaymentIntentParams()
      }
    } finally {
      isHandlingPostRedirectPayment = false
    }
  }

  function getBagTiles() {
    return getCartStore().tilesItem ? tilesStore.providerTiles : []
  }

  function _countTilesToUpload(tiles: CheckoutContextValue['orderTiles']) {
    return tiles.filter((tile) => !photoBankStore.didPhotoUpload(tile.uid))
      .length
  }

  const handleCreatePaymentIntentFail = (error: unknown) => {
    analytics.track('Payment Intent Creation Failed')
    logger.error('create payment intent failed', error)
    setShowGeneralPaymentErrorDialog(true)
  }

  const handleUploadsFailedDismiss = async () => {
    readMobxSnapshot(() => getBagTiles()).forEach(({ id, uid }) => {
      if (uid && failedPhotoUids.includes(uid)) {
        tilesStore.removeTile(id)
        photoBankStore.removePhoto(uid)
      }
    })

    setShowUploadsFailedError(false)
    setFailedPhotoUids([])
    exit()
  }

  const _reportBadCropTiles = async (
    tiles: CheckoutContextValue['orderTiles']
  ) => {
    try {
      logger.info('waiting for bad crop detection task')
      let badCropCount = 0
      let noFaceDetectionCount = 0
      const badCropDetectionPromises = tiles.map(async (t) => {
        const remotePhoto = await readOutsideReaction(() =>
          photoBankStore
            .getRemotePhotoAsync(t.uid)
            .then((photo) =>
              photo
                ? readMobxSnapshot(() => ({ faces: photo.faces }))
                : undefined
            )
        )

        if (remotePhoto?.faces) {
          const {
            isBadCrop,
            badCropReason,
            faceDetectionsBeforeCrop,
            faceDetectionsAfterCrop,
          } = await detectBadCrop(t, remotePhoto.faces)
          analytics.track(EVENT_NAMES.BAD_CROP_DETECTION_COMPLETED, {
            'Is Bad Crop': isBadCrop,
            Reason: badCropReason,
            'Original Face Count': faceDetectionsBeforeCrop.length,
            'Actual Face Count': faceDetectionsAfterCrop.length,
            'Face Count Diff':
              faceDetectionsBeforeCrop.length - faceDetectionsAfterCrop.length,
            'Crop Type': getTileCropType(t),
          })
          badCropCount += isBadCrop ? 1 : 0
        } else {
          noFaceDetectionCount++
        }
      })
      await Promise.all(badCropDetectionPromises)
      const results = {
        'Total Tiles Count': tiles.length,
        'Bad Crop Tiles Count': badCropCount,
        'Good Crop Tiles Count':
          tiles.length - noFaceDetectionCount - badCropCount,
        'No Face Detection Tiles Count': noFaceDetectionCount,
      }
      analytics.track(EVENT_NAMES.BAD_CROP_DETECTION_SUMMARY, results)
      logger.info('Bad crop detection summary', results)
    } catch (e) {
      logger.error('Failed detecting bad crop tiles', e)
    }
  }

  const _getRequiredConfirmPaymentProperties = (
    paymentMethodType: PaymentMethodType
  ) => {
    switch (paymentMethodType) {
      case PaymentMethodType.AFTERPAY:
        // Stripe suggested adding it to avoid the following integration error:
        // "When confirming a PaymentIntent with a `afterpay_clearpay` PaymentMethod and `mandate_data`, `setup_future_usage` is required."
        return {
          mandate_data: {},
        }
      default:
        return {}
    }
  }

  const _confirmPayment = ({
    paymentIntentId,
    address: billingAddress,
    paymentMethodId,
    paymentMethodType,
    expressCheckoutConfirmationToken,
  }: {
    paymentIntentId: string
    address: Address
    paymentMethodId: CreateOrderParams['paymentMethodId']
    paymentMethodType: PaymentMethodType
    expressCheckoutConfirmationToken: CreateOrderParams['expressCheckoutConfirmationToken']
  }) => {
    const { name, address, phone } = convertAddressToStripeFormat(
      billingAddress || {}
    )
    const pathnameParam = `${POST_PAYMENT_QUERY_PATHNAME}=${encodeURIComponent(
      document.location.pathname
    )}`
    const returnUrl = `${window.location.origin}${POST_PAYMENT_URL}?${pathnameParam}`

    const confirmParams = expressCheckoutConfirmationToken
      ? {
          confirmation_token: expressCheckoutConfirmationToken,
          return_url: returnUrl,
        }
      : {
          payment_method: paymentMethodId,
          shipping: address.line1 ? { address, name, phone } : null,
          return_url: returnUrl,
          ..._getRequiredConfirmPaymentProperties(paymentMethodType),
        }

    return mixtilesAxios.post('v4/stripe/confirmPayment', {
      paymentIntentId,
      stripeAccountName: PaymentManager.getStripeAccountName(),
      isBounceEnabled: false,
      confirmParams,
    })
  }

  const _handleConfirmPaymentFail = (error: {
    code?: string
    type?: string
    rawType?: string
  }) => {
    if (error.code === 'payment_intent_authentication_failure') {
      analytics.track('Stripe 3DS Failed To Authenticate (Payment)')
      logger.warning('Stripe 3DS Failed To Authenticate (Payment)', error)
      setShowPaymentIntentAuthenticationError(true)
      return
    }
    if (isStripeCardError(error)) {
      analytics.track(EVENT_NAMES.CARD_DECLINED, {
        ...getStripeErrorProperties(error),
        Source: 'Confirm Payment',
      })
      logger.warning('Card Declined', error)
      setShowCardDeclinedError(true)
      return
    }
    logger.error('Charge failed', error)
    analytics.track(EVENT_NAMES.CHARGE_FAILED, {
      ...getStripeErrorProperties(error),
      Source: 'Confirm Payment',
    })
    setShowGeneralPaymentErrorDialog(true)
  }

  // Add urls of pending uploads from the photo bank to the params which are sent to the backend - for example the tiles / photo book
  // Function also returns the failed photo uids
  const _addUrlsToOrderParams = async (
    tiles: CheckoutContextValue['orderTiles'],
    orderItems: CheckoutContextValue['orderItems'],
    orderPriceSummary: CheckoutSummary
  ) => {
    const failedPhotoUids: string[] = []
    const orderTiles: CheckoutContextValue['orderTiles'] = []
    let orderItemsWithUrls: CheckoutContextValue['orderItems'] | null = null

    for (const tile of tiles) {
      if (isCollage(tile)) {
        const collagePhotos: Record<string, any> = {}
        let collageUpload
        try {
          collageUpload = await generateCollageImageUpload(tile)
        } catch (error) {
          logger.error('Collage composite upload failed', error, {
            tileId: tile.id,
          })
          throw error
        }

        for (const [id, photoTile] of Object.entries<any>(
          tile.collage.photos
        )) {
          const uploadedPhotoUrl = await photoBankStore.getRemotePhotoUrlAsync(
            photoTile.uid
          )
          collagePhotos[id] = { ...photoTile, url: uploadedPhotoUrl }
          if (!uploadedPhotoUrl) {
            failedPhotoUids.push(photoTile.uid)
          }
        }

        Object.keys(collagePhotos).forEach((index) => {
          tile.collage.photos[index] = {
            ...tile.collage.photos[index],
            url: collagePhotos[index].url,
          }
        })

        orderTiles.push({ ...tile, ...collageUpload })
      } else if (isCenterpiece(tile)) {
        orderTiles.push(tile)
      } else {
        const uploadedPhotoUrl = await readOutsideReaction(() =>
          photoBankStore.getRemotePhotoUrlAsync(tile.uid)
        )
        if (!uploadedPhotoUrl) {
          failedPhotoUids.push(tile.uid)
        }
        orderTiles.push({ ...tile, url: uploadedPhotoUrl })
      }
    }

    const hasPhotoBookItems =
      productType === PRODUCT_TYPES.PHOTO_BOOK ||
      orderPriceSummary?.productType === PRODUCT_TYPES.PHOTO_BOOK ||
      orderItems.some((item) => item.itemType === 'Photo Book')
    if (hasPhotoBookItems) {
      orderItemsWithUrls = []
      for (const item of orderItems) {
        if (item.itemType !== 'Photo Book') {
          orderItemsWithUrls.push(item)
          continue
        }
        const { allBookPhotos, remotePhotos, serialized } =
          await resolvePhotoBookMetadata(item, photoBankStore)
        remotePhotos.forEach((remotePhoto, i) => {
          if (!remotePhoto) {
            failedPhotoUids.push(allBookPhotos[i].uid)
          }
        })
        orderItemsWithUrls.push({ ...item, metadata: serialized })
      }
    }

    return { orderTiles, failedPhotoUids, orderItemsWithUrls }
  }

  const createOrder = async ({
    tiles,
    orderItems,
    isGroupOrder,
    groupCheckoutId,
    deliveryPromise,
    hasUpToDateDeliveryDates,
    deliveryRange,
    address,
    paymentMethod,
    paymentMethodId,
    expressCheckoutConfirmationToken,
    orderPriceSummary,
    shouldValidateAddress,
    requestConfig,
    topazLabsEnabled,
    bagAnalytics,
  }: CreateOrderParams) => {
    if (isGroupOrder && !groupCheckoutId) {
      throw new Error('groupCheckoutId is required for group orders')
    }

    const createOrderParams: CreateOrderParams = readMobxSnapshot(() => ({
      tiles,
      orderItems,
      isGroupOrder,
      groupCheckoutId,
      deliveryPromise,
      hasUpToDateDeliveryDates,
      deliveryRange,
      address,
      paymentMethod,
      paymentMethodId,
      expressCheckoutConfirmationToken,
      orderPriceSummary,
      shouldValidateAddress,
      requestConfig,
      topazLabsEnabled,
      bagAnalytics,
    }))
    setOrderRetry({ kind: 'resubmit', createOrderParams })

    analytics.trackScreenView('Completion')

    setOpen(true)
    setCompletionState(null)
    setInitialTilesToUploadCount(
      isGroupOrder
        ? readOutsideReaction(() => _countTilesToUpload(getBagTiles()))
        : 0
    )
    setDeliveryPromise(hasUpToDateDeliveryDates ? deliveryPromise : undefined)
    setDeliveryRange(deliveryRange)

    // Make the progress bar start moving immediately
    setTimeout(() => setCompletionState(CompletionState.PROGRESS), 400)

    // Wait until all tile photos finish uploading
    logger.info('Waiting for all tiles to upload...')
    const isGiftCard = isGiftCardOrder(productType)
    if (!isGiftCard) {
      analytics.track('Uploading Order Photos')
    }
    const waitForPhotosStart = +new Date()
    analytics.track('Order Photos Uploaded', {
      'Time Waited': +new Date() - waitForPhotosStart,
    })

    // Wait until all tasks to finish
    logger.info('Waiting for all async tasks to finish...')
    analytics.track('Waiting For Async Tasks')
    const waitForTasksStart = +new Date()
    await taskManager.waitAllTasks()
    analytics.track('Tasks Completed', {
      'Time Waited': +new Date() - waitForTasksStart,
    })

    let orderPrepResult
    try {
      orderPrepResult = await _addUrlsToOrderParams(
        tiles,
        orderItems,
        orderPriceSummary
      )
    } catch (error) {
      logger.error('Failed to prepare order params', error)
      setShowGeneralPaymentErrorDialog(true)
      return
    }
    const { orderTiles, failedPhotoUids, orderItemsWithUrls } = orderPrepResult

    // "Try again" re-runs createOrder, which resolves photo URLs and serializes
    // photo books again, so the saved retry params must stay unresolved.
    const orderParamsWithUrls: CreateOrderParams = {
      ...createOrderParams,
      tiles: orderTiles,
      orderItems: orderItemsWithUrls || orderItems,
    }

    if (failedPhotoUids.length > 0) {
      setShowUploadsFailedError(true)
      setFailedPhotoUids(failedPhotoUids)
      return
    }

    let paymentIntentId: string | undefined
    if (
      orderPriceSummary.totalPrice > 0 &&
      paymentMethod?.type !== PaymentMethodType.PAYPAL
    ) {
      const paymentIntent = await _handleStripePayment(orderParamsWithUrls)
      if (!paymentIntent) {
        return
      }
      paymentIntentId = paymentIntent.id
    }

    const { order, shouldApplyUpsellPromo } =
      (await _createOrderInBackend({
        createOrderParams: orderParamsWithUrls,
        paymentIntentId,
      })) || {}
    if (order) {
      // Run bad crop detection only after order completed successfully
      if (productType === PRODUCT_TYPES.CLASSIC) {
        const potentialBadCropTiles = orderTiles.filter((t) =>
          shouldDetectBadCrop(t)
        )
        if (potentialBadCropTiles.length > 0) {
          await _reportBadCropTiles(potentialBadCropTiles)
        }
      }
      await _onOrderComplete(
        orderParamsWithUrls,
        paymentIntentId,
        shouldApplyUpsellPromo,
        tiles
      )
    }
  }

  // if order is not with paypal, we first go to the server to create
  // a payment intent (this will not charge the user, only holds the funds)
  const _handleStripePayment = async (
    createOrderParams: CreateOrderParams
  ): Promise<{ id: string } | undefined> => {
    const {
      address,
      paymentMethodId,
      orderPriceSummary,
      paymentMethod,
      expressCheckoutConfirmationToken,
    } = createOrderParams
    if (!paymentMethod) {
      throw new Error('A paid order requires a payment method')
    }

    let paymentIntent: { id: string }
    try {
      const paymentIntentResponse = await PaymentManager.createPaymentIntent(
        orderPriceSummary,
        Boolean(expressCheckoutConfirmationToken)
      )
      if (paymentIntentResponse.data.error) {
        handleCreatePaymentIntentFail(paymentIntentResponse.data.error)
        return
      }
      paymentIntent = paymentIntentResponse.data.success
    } catch (error) {
      handleCreatePaymentIntentFail(error)
      return
    }

    logger.info(`Received payment intent: ${paymentIntent.id}`)
    storeCreateOrderParams(paymentIntent.id, createOrderParams)
    saveCheckoutBackup(paymentIntent.id, createOrderParams)

    if (!isReusablePaymentMethod(paymentMethod.type)) {
      PaymentManager.clearPaymentMethod()
    }

    // (silicakes)
    // 1. Run the confirmPayment flow
    const { data: confirmPaymentData } = await _confirmPayment({
      paymentIntentId: paymentIntent.id,
      address,
      paymentMethodId,
      paymentMethodType: paymentMethod.type,
      expressCheckoutConfirmationToken,
    })

    const {
      error: confirmPaymentError,
      requiresAction,
      clientSecret,
    } = confirmPaymentData

    if (confirmPaymentError) {
      _handleConfirmPaymentFail(confirmPaymentError)
      return
    }

    // (silicakes)
    // 2. Handle any required actions (namely 3rd party validation iframes)
    if (requiresAction) {
      const stripe = stripeRef.current
      if (!stripe) {
        throw new Error('Stripe.js must be loaded to handle a payment action')
      }
      const actionResult = await stripe.handleNextAction({ clientSecret })
      if (actionResult.error) {
        _handleConfirmPaymentFail(actionResult.error)
        return
      }
    }

    return paymentIntent
  }

  const _createOrderInBackend = async ({
    createOrderParams,
    paymentIntentId,
  }: {
    createOrderParams: CreateOrderParams
    paymentIntentId: string | undefined
  }) => {
    logger.info('Creating Order')
    try {
      // Always fetch a fresh token to avoid JWT expiration errors,
      // especially on retries after charge failures.
      const freshToken = await getFreshIdToken()
      const requestConfig = getAuthRequestConfig(freshToken)

      if (createOrderParams.isGroupOrder) {
        const cartPayloads = readMobxSnapshot(() =>
          getCartStore().buildCheckoutPayloads()
        )

        // Resolve photo URLs for each cart payload
        const allFailedPhotoUids: string[] = []
        const cartPayloadsWithResolvedUrls = await Promise.all(
          cartPayloads.map(async (payload) => {
            const { orderTiles, failedPhotoUids, orderItemsWithUrls } =
              await _addUrlsToOrderParams(
                payload.orderTiles ?? [],
                payload.orderItems ?? [],
                { productType: payload.productType }
              )
            allFailedPhotoUids.push(...failedPhotoUids)
            return {
              ...payload,
              orderTiles,
              ...(orderItemsWithUrls ? { orderItems: orderItemsWithUrls } : {}),
            }
          })
        )
        if (allFailedPhotoUids.length > 0) {
          setShowUploadsFailedError(true)
          setFailedPhotoUids(allFailedPhotoUids)
          return
        }
        const result = await createGroupOrder({
          cartPayloads: cartPayloadsWithResolvedUrls,
          checkoutId: createOrderParams.groupCheckoutId!,
          deliveryPromise: createOrderParams.deliveryPromise,
          hasUpToDateDeliveryDates: createOrderParams.hasUpToDateDeliveryDates,
          address: createOrderParams.address,
          paymentMethod: createOrderParams.paymentMethod,
          paymentMethodId: createOrderParams.paymentMethodId,
          paymentIntentId,
          // Each sub-order needs the priced products for ITS OWN order. The
          // effective group summary stores every group-priced product in
          // `products`, each stamped with the order it was priced in, so slice
          // that back into the single-order shape expected by
          // OrderManager.getOrderParams.
          orderPriceSummaries: cartPayloads.map((payload, orderIndex) => ({
            ...createOrderParams.orderPriceSummary,
            productType: payload.productType,
            products: createOrderParams.orderPriceSummary.products.filter(
              (product: GroupPricingProduct) =>
                product.orderIndex === orderIndex
            ),
          })),
          shouldValidateAddress: createOrderParams.shouldValidateAddress,
          topazLabsEnabled: createOrderParams.topazLabsEnabled,
          requestConfig,
          bagAnalytics: createOrderParams.bagAnalytics,
        })
        return {
          order: result.orders[0],
          shouldApplyUpsellPromo: result.shouldApplyUpsellPromo ?? false,
        }
      }

      // we go to the createOrder and pass it the paymentIntent id from
      // createPaymentIntent, this will charge the user and create the order
      // in our database.
      return await OrderManager.createOrder({
        ...createOrderParams,
        requestConfig,
        paymentIntentId,
      })
    } catch (e) {
      const sendOrderErrorEvent = (
        eventName: string,
        extraProps?: Record<string, unknown>
      ) => {
        analytics.track(eventName, {
          'Product Type': getProductTypeForEvent(
            productType,
            createOrderParams.orderItems[0]
          ),
          Error: extractErrorMessage(e),
          ...extraProps,
        })
      }

      const extractedError = extractApiError(e)
      const error = extractedError?.error || e
      switch (error) {
        case 'card_declined':
          sendOrderErrorEvent(EVENT_NAMES.CARD_DECLINED, {
            Source: 'Create Order',
          })
          setShowCardDeclinedError(true)
          logger.warning('Failed to create order - Card Declined')
          break
        case 'charge_error':
          sendOrderErrorEvent(EVENT_NAMES.CHARGE_FAILED, {
            Source: 'Create Order',
          })
          setShowChargeFailedError(true)
          logger.warning('Failed to create order - Charge Failed')
          break
        case 'invalid_coupon':
          sendOrderErrorEvent('Invalid Promo Code')
          setShowInvalidPromoCodeError(true)
          logger.warning('Failed to create order - Invalid Promo Code')
          break
        case 'price_mismatch':
          sendOrderErrorEvent('Price Mismatch')
          setShowPriceMismatchError(true)
          logger.warning('Failed to create order - Price Mismatch')
          break
        case 'checkout_already_used':
          sendOrderErrorEvent(EVENT_NAMES.CHECKOUT_ALREADY_USED)
          setShowGeneralPaymentErrorDialog(true)
          setIsCheckoutAlreadyUsed(true)
          removePaymentIntentParams()
          logger.warning('Failed to create order - Checkout Already Used')
          break
        case 'invalid_email':
          clearUserEmail()
          sendOrderErrorEvent(EVENT_NAMES.INVALID_EMAIL_ORDER_ATTEMPTED, {
            Source: 'Create Order',
          })
          setShowInvalidEmailError(true)
          logger.warning('Failed to create order - Invalid Email')
          break
        default:
          sendOrderErrorEvent('Create Order Failed')
          setShowGeneralPaymentErrorDialog(true)
          sentryService.reportErrorManually(e)
          logger.error(
            'Failed to create order',
            (axios.isAxiosError(e) && e.response?.data) || e,
            { createOrderParams }
          )
          break
      }
    }
  }

  const _onOrderComplete = async (
    createOrderParams: CreateOrderParams,
    paymentIntentId: string | undefined,
    shouldApplyUpsellPromo: boolean,
    tiles: CheckoutContextValue['orderTiles']
  ) => {
    const { deliveryPromise, hasUpToDateDeliveryDates, address } =
      createOrderParams
    logger.info('Order Complete!')
    await account?.reloadAccount()
    handleOrderComplete(true)

    // Prepare collage for upsell before changing state progress to completed
    const hasTilesUpsell =
      productType === PRODUCT_TYPES.CLASSIC &&
      tiles &&
      experimentManager.isEnabled('tile-upsell')
    let collageUpsell:
      | Awaited<ReturnType<typeof prepareUpsellCollage>>
      | undefined
    if (hasTilesUpsell) {
      const photoTiles = tiles.filter((tile) => isPhoto(tile))
      collageUpsell =
        IS_COLLAGE_CREATION_AVAILABLE && photoTiles.length >= 2
          ? await prepareUpsellCollage(photoTiles)
          : undefined
    }

    setCompletionState(CompletionState.PROGRESS_COMPLETED)
    setTimeout(() => {
      resetOrderSessionId()
      if (paymentIntentId) {
        clearCreateOrderParams(paymentIntentId)
      }
      clearTileWarningsCache()
      clearCartId(FLOWS.TILE_DESIGNER)
      clearCartId(FLOWS.CART)

      // Check if we should redirect to the order summary page based on the tile-upsell experiment
      if (hasTilesUpsell) {
        const withSign = !tiles.some((tile) => isCenterpiece(tile))
        navigate(ORDER_SUMMARY_V2_URL, {
          state: {
            collage: collageUpsell,
            eta:
              deliveryPromise && hasUpToDateDeliveryDates
                ? isoToDeliveryDate(deliveryPromise.promisedArrivalDate.iso)
                : null,
            address: address.street,
            analytics: {
              'Upsell Offer Type': 'Single Tiles Post Purchase',
              'Last Order Tiles': tiles.length,
              'Has Collage': !!collageUpsell,
              'Collage Text': collageUpsell?.collageText?.text,
            },
            shouldApplyUpsellPromo,
            shouldSkipAnimation: false,
            withSign,
          },
        })
        setOpen(false)
        return
      }

      setCompletionState(CompletionState.DONE)
      setShowSuccessDialog(true)
    }, 2000)
    if (paymentIntentId) {
      storeCompletedPaymentIntentIds(paymentIntentId)
    }
    removePaymentIntentParams()
  }

  const exit = (destinationUrl?: string) => {
    setOpen(false)
    setShowSuccessDialog(false)
    if (destinationUrl) {
      navigate(destinationUrl)
    }
  }

  const handleRetryOrderClick = () => {
    analytics.track('Retry Order Tapped')
    if (!orderRetry) {
      throw new Error('Cannot retry an order without its params')
    }
    if (
      orderRetry.kind === 'reopen-checkout' ||
      orderRetry.createOrderParams.expressCheckoutConfirmationToken
    ) {
      backToCheckout()
      return
    }
    handleCloseDialogs()
    setTimeout(() => createOrder(orderRetry.createOrderParams), RETRY_INTERVAL)
  }

  const handleCloseDialogs = () => {
    setOpen(false)
    setShowPaymentIntentAuthenticationError(false)
    setShowCardDeclinedError(false)
    setShowChargeFailedError(false)
    setShowInvalidPromoCodeError(false)
    setShowPriceMismatchError(false)
    setShowGeneralPaymentErrorDialog(false)
    setShowInvalidEmailError(false)
    setIsCheckoutAlreadyUsed(false)
    setShowUploadsFailedError(false)
  }

  const closeCompletionPage = () => {
    handleCloseDialogs()
    setCompletionState(null)
  }

  const talkToUsPressed = () => {
    closeCompletionPage()
    intercomService.show()
  }

  const backToCheckout = () => {
    closeCompletionPage()
    openCheckout()
  }

  const removeCouponAndBackToCheckout = () => {
    promoCodeManager.removeDiscountCoupon()
    backToCheckout()
  }

  function renderErrorDialogs() {
    const paymentIntentAuthenticationError = (
      <MixtilesDialog
        open={showPaymentIntentAuthenticationError}
        fullScreen={false}
        onDismiss={handleCloseDialogs}
        transition="grow"
      >
        <BasicDialogContent
          icon="warning"
          title={t('order.checkout.payment_errors.authentication_failed_title')}
          text={t('order.checkout.payment_errors.authentication_failed_text')}
        />
        <BasicDialogButton action={backToCheckout}>
          {t('order.checkout.payment_errors.add_new_card')}
        </BasicDialogButton>
        <BasicDialogButton action={talkToUsPressed}>
          {t('general.talk_to_us')}
        </BasicDialogButton>
      </MixtilesDialog>
    )
    const cardDeclinedError = (
      <MixtilesDialog
        open={showCardDeclinedError}
        fullScreen={false}
        onDismiss={handleCloseDialogs}
        transition="grow"
      >
        <BasicDialogContent
          icon="warning"
          title={t('order.checkout.payment_errors.card_declined_title_2')}
          text={t('order.checkout.payment_errors.card_declined_text')}
        />
        <BasicDialogButton action={backToCheckout}>
          {t('order.checkout.payment_errors.add_new_card')}
        </BasicDialogButton>
        <BasicDialogButton action={talkToUsPressed}>
          {t('general.talk_to_us')}
        </BasicDialogButton>
      </MixtilesDialog>
    )
    const chargeFailed = (
      <MixtilesDialog
        open={showChargeFailedError}
        fullScreen={false}
        onDismiss={handleCloseDialogs}
        transition="grow"
      >
        <BasicDialogContent
          icon="warning"
          title={t('order.checkout.payment_errors.charge_failed_title')}
          text={t('order.checkout.payment_errors.charge_failed_text')}
        />
        <BasicDialogButton action={backToCheckout}>
          {t('order.checkout.payment_errors.add_new_card')}
        </BasicDialogButton>
        <BasicDialogButton action={talkToUsPressed}>
          {t('general.talk_to_us')}
        </BasicDialogButton>
      </MixtilesDialog>
    )
    const invalidPromoCodeError = (
      <MixtilesDialog
        open={showInvalidPromoCodeError}
        fullScreen={false}
        onDismiss={handleCloseDialogs}
        transition="grow"
      >
        <BasicDialogContent
          icon="warning"
          title="Promo code expired"
          text="Seems like the promo code is no longer valid. You can try entering a new one, or talk to us!"
        />
        <BasicDialogButton action={removeCouponAndBackToCheckout}>
          {t('general.got_it')}
        </BasicDialogButton>
        <BasicDialogButton action={talkToUsPressed}>
          {t('general.talk_to_us')}
        </BasicDialogButton>
      </MixtilesDialog>
    )
    const priceMismatchError = (
      <MixtilesDialog
        open={showPriceMismatchError}
        fullScreen={false}
        onDismiss={handleCloseDialogs}
        transition="grow"
      >
        <BasicDialogContent
          icon="warning"
          title={t('order.checkout.payment_errors.payment_error_title')}
          text={t('order.checkout.payment_errors.payment_error_text')}
        />
        <BasicDialogButton action={backToCheckout}>
          {t('general.got_it')}
        </BasicDialogButton>
        <BasicDialogButton action={talkToUsPressed}>
          {t('general.talk_to_us')}
        </BasicDialogButton>
      </MixtilesDialog>
    )
    const invalidEmailError = (
      <MixtilesDialog
        open={showInvalidEmailError}
        fullScreen={false}
        onDismiss={handleCloseDialogs}
        transition="grow"
      >
        <BasicDialogContent
          icon="warning"
          title={t('order.checkout.payment_errors.invalid_email_title')}
          text={t('order.checkout.payment_errors.invalid_email_text')}
        />
        <BasicDialogButton action={backToCheckout}>
          {t('general.got_it')}
        </BasicDialogButton>
      </MixtilesDialog>
    )
    const generalOrderError = (
      <CompleteOrderErrorDialog
        show={showGeneralPaymentErrorDialog}
        onDismiss={() => backToCheckout()}
        onTryAgain={handleRetryOrderClick}
        talkToUsPressed={talkToUsPressed}
        errorTitle="order.checkout.payment_errors.order_error_title"
        errorText="order.checkout.payment_errors.order_error_text"
        dismissOnly={isCheckoutAlreadyUsed}
      />
    )
    return (
      <>
        {paymentIntentAuthenticationError}
        {cardDeclinedError}
        {chargeFailed}
        {invalidPromoCodeError}
        {priceMismatchError}
        {invalidEmailError}
        {generalOrderError}
      </>
    )
  }

  function getOrderCompletedText() {
    if (
      deliveryRange &&
      deliveryRange.startDateISO &&
      deliveryPromise &&
      shouldShowDeliveryRange(deliveryPromise)
    ) {
      return t('order.done.delivery_time', {
        v1: formatIsoDateRange(
          deliveryRange.startDateISO,
          deliveryRange.endDateISO
        ),
      })
    } else {
      const deliveryBy = deliveryPromise
        ? isoToDeliveryDate(deliveryPromise.promisedArrivalDate.iso)
        : null
      return deliveryBy
        ? t('order.done.delivery_time', { v1: deliveryBy })
        : t('order.done.confirmation_email')
    }
  }

  const handlePostPurchaseDismiss = () => {
    if (attributionSurveyManager.didAnswerSurvey()) {
      exit(props.isCartFlow ? HOME_URL : undefined)
    } else {
      setShowSuccessDialog(false)
      setOpen(false)
      setShowAttributionSurvey(true)
    }
  }

  const handleSubmitAttributionSurvey: ComponentProps<
    typeof PostPurchaseAttribution
  >['onSubmit'] = ({ channelName, subChannel }) => {
    attributionSurveyManager.trackAttributionSurveyAnalytics({
      channelName,
      subChannel,
      source: ATTRIBUTION_SOURCE.postPurchase,
    })
    setShowAttributionSurvey(false)
    exit(props.isCartFlow ? HOME_URL : undefined)
  }

  useImperativeHandle(ref, () => ({ createOrder, handlePostRedirectPayment }))

  let uploadProgress = initialTilesToUploadCount
    ? 20 +
      Math.round(
        ((initialTilesToUploadCount - _countTilesToUpload(getBagTiles())) /
          initialTilesToUploadCount) *
          70
      ) +
      extraProgress
    : 90

  if (completionState === null) {
    uploadProgress = 0
  }

  const finishProgressBar =
    completionState === CompletionState.PROGRESS_COMPLETED

  const showProgressBar =
    completionState === CompletionState.PROGRESS ||
    completionState === CompletionState.PROGRESS_COMPLETED
  const progressBoxClass = classNames('step progress-box', {
    hidden: !showProgressBar,
  })

  const orderCompletedText = getOrderCompletedText()

  if (showUploadsFailedError) {
    return (
      <UploadsFailedPopup
        show={showUploadsFailedError}
        onDismiss={handleUploadsFailedDismiss}
      />
    )
  }

  return (
    <>
      {/* disableScrollLock: MUI locks once per container, and this dialog opens first
          and stays open for the whole flow — so it would snapshot a Base UI sheet's
          `overflow: hidden` and re-apply it for good on close (COR-1325). */}
      <Dialog
        className="complete-order-full-dialog"
        fullScreen
        open={open}
        transitionDuration={{ enter: 500, exit: 250 }}
        disableEnforceFocus
        disableScrollLock
      >
        <div className="complete-order-content">
          <div className={progressBoxClass}>
            <div className="complete-order-text">
              {t('order.done.please_hold')}
            </div>
            <div className="progress-bar">
              <S.Progress
                $finishProgressBar={finishProgressBar}
                $uploadProgress={uploadProgress}
              />
            </div>
          </div>
        </div>
        {renderErrorDialogs()}
        <PostPurchaseSuccess
          show={showSuccessDialog}
          onDismiss={handlePostPurchaseDismiss}
          altText={orderCompletedText}
        />
      </Dialog>
      <PostPurchaseAttribution
        show={showAttributionSurvey}
        source={ATTRIBUTION_SOURCE.postPurchase}
        onSubmit={handleSubmitAttributionSurvey}
      />
    </>
  )
}

export default observer(forwardRef(CompletingOrder))
